import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { ANALYTICS_CATALOG } from '../graphql/analytics';
import {
  ANALYTICS_WORKFLOWS,
  ANALYTICS_EXPORT,
  DELETE_ANALYTICS_WORKFLOW,
  RUN_ANALYTICS_WORKFLOW,
  UPDATE_ANALYTICS_WORKFLOW
} from '../graphql/analytics_workflows';
import WorkflowEditor from '../components/analytics/workflow/WorkflowEditor';
import ToolsPanel from '../components/analytics/tools/ToolsPanel';
import M3TabBar from '../components/M3TabBar';
import M3Card from '../components/M3Card';
import M3Chip from '../components/M3Chip';
import M3LinearProgress from '../components/M3Progress';
import M3Dialog, { DialogTitle, DialogBody, DialogActions } from '../components/M3Dialog';
import { downloadExport } from '../components/analytics/downloadExport';
import { isRunning, metasByKey, outputNodes } from '../components/analytics/workflow/graphModel';

/** Analytics workspace: single workflow editor with overview and results tabs.
 * Expects `id` from the URL via useParams. */
export default function AnalyticsWorkspace() {
  const navigate = useNavigate();
  const { id: workflowId } = useParams();
  const [activeTab, setActiveTab] = useState('workflow');
  const [workflowTitle, setWorkflowTitle] = useState('');
  const [renameDialog, setRenameDialog] = useState(false);
  const [renameTitle, setRenameTitle] = useState('');
  const { data: catalogData } = useQuery(ANALYTICS_CATALOG);

  const { data: workflowData, refetch: refetchWorkflow } = useQuery(ANALYTICS_WORKFLOWS, {
    variables: { projectId: null },
    skip: !workflowId,
    fetchPolicy: 'cache-and-network'
  });

  const workflows = (workflowData && workflowData.analyticsWorkflows) || [];
  const activeWorkflow = workflows.find((w) => w.id === workflowId);
  const metas = useMemo(
    () =>
      metasByKey(
        (catalogData && catalogData.analyticsCatalog
          ? catalogData.analyticsCatalog.nodeTypes
          : []) || []
      ),
    [catalogData]
  );

  const [updateWorkflow] = useMutation(UPDATE_ANALYTICS_WORKFLOW);
  const [deleteWorkflow] = useMutation(DELETE_ANALYTICS_WORKFLOW);
  const [runWorkflow] = useMutation(RUN_ANALYTICS_WORKFLOW);
  const [exportWorkflow] = useMutation(ANALYTICS_EXPORT);

  useEffect(() => {
    if (!activeWorkflow || !isRunning(activeWorkflow.status)) return undefined;
    const timer = setInterval(refetchWorkflow, 2000);
    return () => clearInterval(timer);
  }, [activeWorkflow && activeWorkflow.status, refetchWorkflow]);

  // Sync title from workflow data
  useEffect(() => {
    if (activeWorkflow) setWorkflowTitle(activeWorkflow.title);
  }, [activeWorkflow]);

  const openRenameDialog = () => {
    setRenameTitle(activeWorkflow.title);
    setRenameDialog(true);
  };
  const handleRename = () => {
    if (!renameTitle.trim()) return;
    updateWorkflow({ variables: { input: { id: activeWorkflow.id, title: renameTitle.trim() } } })
      .then(() => refetchWorkflow())
      .finally(() => {
        setRenameDialog(false);
        setRenameTitle('');
      });
  };

  const persist = (graph) =>
    updateWorkflow({ variables: { input: { id: activeWorkflow.id, graph } } });
  const rename = (title) =>
    updateWorkflow({ variables: { input: { id: activeWorkflow.id, title } } }).then(() =>
      refetchWorkflow()
    );
  const run = () =>
    runWorkflow({ variables: { input: { id: activeWorkflow.id } } }).then(() => refetchWorkflow());
  const exportNode = (nodeId, format) =>
    exportWorkflow({
      variables: { input: { workflowId: activeWorkflow.id, nodeId, format } }
    }).then((res) => downloadExport(res.data.analyticsExport));

  // Overview stats
  const workflowStats = activeWorkflow
    ? {
        totalNodes: activeWorkflow.graph?.nodes?.length || 0,
        outputNodes: activeWorkflow.graph ? outputNodes(activeWorkflow.graph, metas).length : 0,
        status: activeWorkflow.status,
        lastRun: activeWorkflow.result?.ranAt,
        hasError: !!activeWorkflow.error
      }
    : null;

  if (!activeWorkflow) {
    return (
      <div className="analytics-workspace">
        <header className="analytics-workspace-header">
          <button type="button" className="btn btn-text" onClick={() => navigate('/analytics')}>
            ← Back to projects
          </button>
          <h1>{workflowTitle || 'Loading…'}</h1>
        </header>
        <M3Card variant="outlined">
          <M3Card.Body>
            <p className="text-muted">No workflow found. Go back to the dashboard.</p>
          </M3Card.Body>
        </M3Card>
      </div>
    );
  }

  const renderOverviewContent = () => {
    const statusColors = {
      pending: 'warning',
      running: 'primary',
      done: 'success',
      failed: 'error'
    };

    return (
      <>
        <M3Card variant="outlined">
          <M3Card.Header title="Workflow Overview" />
          <M3Card.Body>
            <div className="overview-grid">
              <div className="overview-stat">
                <div className="overview-stat-value">{workflowStats.totalNodes}</div>
                <div className="overview-stat-label">Nodes</div>
              </div>
              <div className="overview-stat">
                <div className="overview-stat-value">{workflowStats.outputNodes}</div>
                <div className="overview-stat-label">Outputs</div>
              </div>
              <div className="overview-stat">
                <M3Chip
                  variant="assist"
                  label={activeWorkflow.status}
                  color={statusColors[activeWorkflow.status] || 'default'}
                />
                <div className="overview-stat-label">Status</div>
              </div>
              {workflowStats.lastRun && (
                <div className="overview-stat">
                  <div className="overview-stat-value overview-stat-date">
                    {new Date(workflowStats.lastRun).toLocaleString()}
                  </div>
                  <div className="overview-stat-label">Last Run</div>
                </div>
              )}
            </div>
            {workflowStats.hasError && (
              <div className="alert alert-warning mt-3">
                <strong>Error:</strong> {activeWorkflow.error}
              </div>
            )}
          </M3Card.Body>
        </M3Card>

        {activeWorkflow.graph?.nodes?.length > 0 && (
          <M3Card variant="outlined">
            <M3Card.Header title="Node Summary" />
            <M3Card.Body>
              <table className="viz-table">
                <thead>
                  <tr>
                    <th>Node</th>
                    <th>Type</th>
                    <th>ID</th>
                  </tr>
                </thead>
                <tbody>
                  {activeWorkflow.graph.nodes.map((node) => (
                    <tr key={node.id}>
                      <td>{node.label || node.id}</td>
                      <td>{node.type}</td>
                      <td className="text-end">{node.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </M3Card.Body>
          </M3Card>
        )}
      </>
    );
  };

  const renderResultsContent = () => {
    if (!activeWorkflow.result || Object.keys(activeWorkflow.result.nodes || {}).length === 0) {
      return (
        <M3Card variant="outlined">
          <M3Card.Body>
            <p className="text-muted">No results yet. Run the workflow to generate output.</p>
            <button
              type="button"
              className="btn btn-primary mt-3"
              onClick={run}
              disabled={isRunning(activeWorkflow.status)}
            >
              {isRunning(activeWorkflow.status) ? 'Running…' : 'Run Workflow'}
            </button>
          </M3Card.Body>
        </M3Card>
      );
    }

    const ResultsPanel = React.lazy(() => import('../components/analytics/workflow/ResultsPanel'));

    return (
      <M3Card variant="outlined">
        <M3Card.Body>
          <React.Suspense fallback={<M3LinearProgress indeterminate />}>
            <ResultsPanel
              graph={activeWorkflow.graph}
              metas={metas}
              workflow={activeWorkflow}
              onExport={exportNode}
            />
          </React.Suspense>
        </M3Card.Body>
      </M3Card>
    );
  };

  return (
    <div className="analytics-workspace">
      <header className="analytics-workspace-header">
        <div className="analytics-workspace-header-left">
          <button type="button" className="btn btn-text" onClick={() => navigate('/analytics')}>
            ← Back
          </button>
          <h1 className="workspace-title">
            {workflowTitle}
            <button
              type="button"
              className="workspace-rename-btn"
              onClick={openRenameDialog}
              title="Rename"
            >
              <span className="material-symbols-rounded">edit</span>
            </button>
          </h1>
        </div>
        <select
          className="workspace-select"
          value={activeWorkflow.id}
          onChange={(e) => {
            window.location.href = `/analytics/workflows/${e.target.value}`;
          }}
        >
          {workflows.map((wf) => (
            <option key={wf.id} value={wf.id}>
              {wf.title}
            </option>
          ))}
        </select>
      </header>

      <M3TabBar
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'workflow', label: 'Workflow' },
          { key: 'tools', label: 'Tools' },
          { key: 'results', label: 'Results' }
        ]}
        activeKey={activeTab}
        onTabChange={setActiveTab}
        mode="scrollable"
      />

      {activeTab === 'workflow' ? (
        <WorkflowEditor
          key={activeWorkflow.id}
          workflow={activeWorkflow}
          metas={metas}
          onPersist={persist}
          onRename={rename}
          onRun={run}
          onDelete={() =>
            deleteWorkflow({ variables: { input: { id: activeWorkflow.id } } }).then(() =>
              navigate('/analytics')
            )
          }
          onExport={exportNode}
        />
      ) : activeTab === 'tools' ? (
        <ToolsPanel />
      ) : activeTab === 'overview' ? (
        renderOverviewContent()
      ) : (
        <React.Suspense fallback={<M3LinearProgress indeterminate />}>
          {renderResultsContent()}
        </React.Suspense>
      )}

      {/* Rename Dialog */}
      <M3Dialog
        open={renameDialog}
        onClose={() => {
          setRenameDialog(false);
          setRenameTitle('');
        }}
        title="Rename Workflow"
        size="small"
      >
        <DialogBody>
          <input
            type="text"
            className="sidebar-input"
            placeholder="Workflow title…"
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename();
            }}
            autoFocus
          />
        </DialogBody>
        <DialogActions
          primary={{ label: 'Rename', onClick: handleRename }}
          secondary={{
            label: 'Cancel',
            onClick: () => {
              setRenameDialog(false);
              setRenameTitle('');
            }
          }}
        />
      </M3Dialog>
    </div>
  );
}
